import moment, {Moment} from 'moment-timezone';

export const DATE_FORMAT = 'MM/DD/YYYY';
export const DATE_TIME_FORMAT = 'MM/DD/YYYY hh:mm A';

const DATE_FORMATS = {
  date: DATE_FORMAT,
  'date-time': DATE_TIME_FORMAT,
} as const;

export const DATE_INPUT_REGEX =
  /^([0-9]+|[0-9]*\/[0-9]*\/?|[0-9]*\/[0-9]*\/[0-9]+)$/;
export const DATE_TIME_INPUT_REGEX =
  /^(([0-9]{1,8}|[0-9]*\/[0-9]*\/?|[0-9]*\/[0-9]*\/[0-9]+)[\s\S]*)$/;
export const PARTIAL_DATE_INPUT_REGEX =
  /^([0-9]{2}\/|[0-9]{1,2}$|[0-9]{2}\/[0-9]{1,2}$)$/;

const DATE_REGEX_STRICT = /^([0-9]{2})\/([0-9]{2})\/([0-9]{4})$/;
const DATE_REGEX_LOOSE = /^([0-9]{1,2})\/([0-9]{1,2})\/([0-9]{1,4})$/;

const DATE_TIME_REGEX_STRICT =
  /^([0-9]{2})\/([0-9]{2})\/([0-9]{4}) ([0-9]{1,2}):([0-9]{2}) (AM|PM)$/i;
const DATE_TIME_REGEX_LOOSE =
  /^([0-9]{1,2})\/([0-9]{1,2})\/([0-9]{1,4}) ([0-9]{1,2}):([0-9]{2}) (AM|PM)$/i;

const DATE_REGEXES = {
  date: {
    strict: DATE_REGEX_STRICT,
    loose: DATE_REGEX_LOOSE,
  },
  'date-time': {
    strict: DATE_TIME_REGEX_STRICT,
    loose: DATE_TIME_REGEX_LOOSE,
  },
} as const;

function dateFormat(format: string) {
  return DATE_FORMATS[format === 'date-time' ? 'date-time' : 'date'];
}

function dateRegex(format: string, strict?: boolean) {
  return DATE_REGEXES[format === 'date-time' ? 'date-time' : 'date'][
    strict ? 'strict' : 'loose'
  ];
}

export const UTC = 'UTC';

/**
 * Site timezones come from remote site data, which can omit the field entirely or hold a name
 * that is not a valid IANA zone. Both cases break consumers downstream, so coerce them to UTC.
 */
export function normalizeSiteTimezone(
  timezone: string | null | undefined
): string {
  if (typeof timezone !== 'string') {
    return UTC;
  }

  // Validated against moment's zone database rather than Intl's, because every consumer below
  // resolves the zone through `moment.tz`. Intl accepts offset-style names such as `+05:00`
  // that moment has no data for, which would silently fall back to UTC on every format call.
  return moment.tz.zone(timezone) ? timezone : UTC;
}

export function parse(
  s: string,
  // @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type '{ format: string; strict?: boolean | undefined; timezone: string; }': format, timezone
  {
    format = '',
    strict = true,
    timezone = UTC,
  }: {
    format: string;
    strict?: boolean;
    timezone: string;
  } = {}
) {
  if (typeof s !== 'string') {
    return null;
  }
  if (!s) {
    return '';
  }
  if (!dateRegex(format, strict).test(s)) {
    return null;
  }

  const m = moment.utc(s, dateFormat(format)).tz(timezone);

  if (m.isValid()) {
    return m.subtract(m.utcOffset(), 'm').toISOString();
  } else {
    return null;
  }
}

export function unparse(
  s: string,
  // @ts-expect-error - TS2739 - Type '{}' is missing the following properties from type '{ format: string; timezone: string; }': format, timezone
  {
    format = '',
    timezone = UTC,
  }: {
    format: string;
    timezone: string;
  } = {}
) {
  if (typeof s !== 'string') {
    return null;
  }

  const m = moment.utc(s, moment.ISO_8601).tz(timezone);

  if (m !== null && m.isValid()) {
    return m
      .format(dateFormat(format))
      .replace(/^([0-9]+\/[0-9]+\/)0*([0-9]+(?:\s|$))/, '$1$2');
  } else {
    return s;
  }
}

export function matchesFormat(
  s: string,
  {
    format,
  }: {
    format: string;
  }
) {
  return typeof s === 'string' && dateRegex(format).test(s);
}

export function formatDateString(dateString: string) {
  // Split into separate date components
  const dateComponents = dateString
    .split('/')
    .filter((component) => component !== '');
  const numericDateValue = dateComponents.join('');

  if (dateString.endsWith('/')) {
    // Account for single digit month/day date components and pad them with leading zeros
    const paddedComponents = dateComponents.map((dateComponent, index) =>
      index < 2 && dateComponent.length === 1
        ? '0' + dateComponent
        : dateComponent
    );

    // Join with separator and preserve trailing separator
    return paddedComponents.join('/') + '/';
  }

  // Format numeric input values (e.g. 12232025 -> 12/23/2025)
  let formattedDateString = '';
  // Month component
  if (numericDateValue.length >= 1) {
    formattedDateString = numericDateValue.slice(0, 2);
  }

  // Day component
  if (numericDateValue.length > 2) {
    formattedDateString += '/' + numericDateValue.slice(2, 4);
  }

  // Year component
  if (numericDateValue.length > 4) {
    formattedDateString += '/' + numericDateValue.slice(4);
  }

  return formattedDateString;
}

/**
 * Converts a date to a string using a format string.
 * @param  {Date}     date      The date value.
 * @param  {String}   format    The format string, e.g. 'short', 'fullDate', 'longDate', etc
 * @param  {Object}   options   .timezone The moment-compatible timezone string to format with.
 * @return {String}             The date value, after the format has been applied.
 */
export function toString(
  date: string,
  format: string,
  // @ts-expect-error - TS2741 - Property 'timezone' is missing in type '{}' but required in type '{ timezone: string; }'.
  {
    timezone,
  }: {
    timezone: string;
  } = {}
) {
  // Don't try to filter invalid values or when the format is not specified
  if (!date || !format) {
    return date;
  }

  return moment
    .utc(date)
    .tz(timezone || UTC)
    .format(format);
}

/**
 * Returns a human-readable "time ago" string based on a given date.
 * @param  {Date}   date  A date value.
 * @return {String}       E.g. "6 hours ago" or "4 days ago"
 */
export function fromNow(date: string | number | Date) {
  return moment(date).fromNow();
}

export function isISOString(s: string) {
  return moment(s).toISOString() === s;
}

/**
 * Returns the difference between two dates in the specified unit.
 *
 * @param {string | number | Date | Moment} date1
 * @param {string | number | Daet| Moment} date2
 * @param {moment.unitOfTime.Diff} unit
 * @returns {number}
 */
export function dateDifference(
  date1: string | number | Date | Moment,
  date2: string | number | Date | Moment,
  unit: moment.unitOfTime.Diff
): number {
  return moment(date1).diff(date2, unit);
}

/**
 * Returns the current Partner Program year based on the current date.
 *
 * The Partner Program year starts in February and ends in the following January.
 * For example, the period of 2/1/24-1/31/25 is considered the 2024 Partner Program Year.
 *
 * @param now - Optional date to calculate the Partner Program year for
 * @returns {number} The Partner Program year
 */
export function getPartnerProgramYear(
  now?: string | number | Moment | undefined
): number {
  return moment.utc(now).subtract(1, 'month').year();
}

/**
 * Returns the last date of a fiscal year
 *
 * @param fiscalYear - Override the current fiscal year
 */
export const getFiscalYearEndDate = (fiscalYear?: number) => {
  return moment
    .utc()
    .year(fiscalYear ?? getPartnerProgramYear())
    .month(0)
    .endOf('month')
    .add(1, 'year')
    .toDate();
};
